/*
 * ============================================================
 * Hoito Hero Banner — kaymalı (slider) hero banner motoru.
 *
 * Bağımlılık yok, vanilla custom element. Tek bir <hoito-hero-banner>
 * elementini sürer. İki geçiş modu:
 *   - "slide": klonlu sonsuz döngü (rewind görünmez), translateX track.
 *   - "fade":  üst üste binmiş slide'lar, opacity geçişi.
 *
 * Özellikler: otomatik oynatma + ilerleme çubuğu, hover/focus/sekme
 * görünürlüğünde duraklatma, dokunmatik kaydırma (pointer), ok tuşları,
 * noktalar, oklar, prefers-reduced-motion desteği.
 *
 * DOM sözleşmesi (section markup'ından gelir):
 *   data-autoplay="true|false"  data-speed="ms"  data-transition="slide|fade"
 *   [data-hero-viewport] [data-hero-track] [data-hero-slide]
 *   [data-hero-dot data-index] [data-hero-prev] [data-hero-next]
 *   [data-hero-progress] [data-hero-live]
 * ============================================================
 */
class HoitoHeroBanner extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.viewport = this.querySelector('[data-hero-viewport]');
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.progressBar = this.querySelector('[data-hero-progress]');
    this.live = this.querySelector('[data-hero-live]');

    this.count = this.slides.length;
    this.mode = this.dataset.transition === 'fade' ? 'fade' : 'slide';
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;

    this.index = 0; // gerçek (kullanıcıya görünen) slide index'i
    this.pos = 0; // track pozisyonu (slide modunda klonlar dahil)
    this.paused = false;
    this.dragging = false;

    // Tek slide: kontrol yok, statik göster.
    if (this.count <= 1) {
      this.toggleControls(false);
      if (this.slides[0]) this.slides[0].classList.add('is-active');
      return;
    }

    if (this.mode === 'slide') this.setupClones();
    this.bindEvents();
    this.goTo(0, false);

    if (this.autoplay) this.start();
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  /* ---------- Slide modu: sonsuz döngü için klonlar ---------- */
  setupClones() {
    const firstClone = this.slides[0].cloneNode(true);
    const lastClone = this.slides[this.count - 1].cloneNode(true);
    [firstClone, lastClone].forEach((c) => {
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-hero-clone', '');
      c.removeAttribute('id');
    });
    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.slides[0]);
    // Klonlar eklendikten sonra gerçek slide'lar 1..count pozisyonlarında.
    this.pos = 1;
    this.track.addEventListener('transitionend', (e) => {
      if (e.target !== this.track || e.propertyName !== 'transform') return;
      this.handleLoopReset();
    });
  }

  handleLoopReset() {
    if (this.pos === this.count + 1) {
      this.jumpTo(1);
    } else if (this.pos === 0) {
      this.jumpTo(this.count);
    }
  }

  /* ---------- Navigasyon ---------- */
  realFromPos(pos) {
    return ((pos - 1) % this.count + this.count) % this.count;
  }

  translate(pos, animate) {
    this.pos = pos;
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = `translate3d(${-pos * 100}%, 0, 0)`;
    if (!animate) {
      // reflow zorla, sonraki geçişlerin atlamamasını sağla
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
  }

  jumpTo(pos) {
    this.translate(pos, false);
    this.index = this.realFromPos(pos);
    this.updateUI();
  }

  goTo(realIndex, animate = true, fromUser = false) {
    realIndex = ((realIndex % this.count) + this.count) % this.count;
    this.index = realIndex;

    if (this.mode === 'fade') {
      this.updateUI();
    } else {
      this.translate(realIndex + 1, animate);
      this.updateUI();
    }

    this.announce();
    if (this.autoplay && !this.paused) this.schedule();
  }

  next(fromUser = false) {
    if (this.mode === 'fade') {
      this.goTo(this.index + 1, true, fromUser);
    } else {
      this.translate(this.pos + 1, true);
      this.index = this.realFromPos(this.pos);
      this.updateUI();
      this.announce();
      if (this.autoplay && !this.paused) this.schedule();
    }
  }

  prev(fromUser = false) {
    if (this.mode === 'fade') {
      this.goTo(this.index - 1, true, fromUser);
    } else {
      this.translate(this.pos - 1, true);
      this.index = this.realFromPos(this.pos);
      this.updateUI();
      this.announce();
      if (this.autoplay && !this.paused) this.schedule();
    }
  }

  updateUI() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      // Klon olmayan slide'larda erişilebilirlik durumu
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    // Aktif slide'ın metin temasını üst elemana yansıt (sayaç rengi vb.)
    const activeSlide = this.slides[this.index];
    if (activeSlide) {
      this.dataset.activeTheme = activeSlide.getAttribute('data-theme') || 'light';
    }
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('tabindex', active ? '0' : '-1');
    });
  }

  announce() {
    const cur = this.index + 1;
    if (this.live) {
      this.live.textContent = `${cur} / ${this.count}`;
    }
    if (this.counterEl === undefined) this.counterEl = this.querySelector('[data-hero-current]');
    if (this.counterEl) this.counterEl.textContent = String(cur).padStart(2, '0');
  }

  /* ---------- Otomatik oynatma + ilerleme çubuğu ---------- */
  start() {
    this.paused = false;
    this.schedule();
  }

  schedule() {
    this.stopTimer();
    this.runProgress();
    this.timer = setTimeout(() => this.next(false), this.speed);
  }

  stopTimer() {
    clearTimeout(this.timer);
  }

  runProgress() {
    if (!this.progressBar) return;
    this.progressBar.style.animation = 'none';
    void this.progressBar.offsetWidth; // reflow → animasyonu sıfırla
    this.progressBar.style.animation = `hoito-hero-progress ${this.speed}ms linear forwards`;
    this.progressBar.style.animationPlayState = 'running';
  }

  pauseProgress() {
    if (this.progressBar) this.progressBar.style.animationPlayState = 'paused';
  }

  resumeProgress() {
    if (this.progressBar) this.progressBar.style.animationPlayState = 'running';
  }

  pause() {
    if (!this.autoplay) return;
    this.paused = true;
    this.stopTimer();
    this.pauseProgress();
  }

  resume() {
    if (!this.autoplay || this.dragging) return;
    if (!this.paused) return;
    this.paused = false;
    this.resumeProgress();
    // Kalan süreyi tam olarak takip etmek yerine tur'u yeniden başlat — basit ve tutarlı.
    this.timer = setTimeout(() => this.next(false), this.remainingApprox());
  }

  remainingApprox() {
    return this.speed; // duraklamadan sonra tam tur (UX olarak yeterince iyi)
  }

  /* ---------- Olaylar ---------- */
  bindEvents() {
    this.toggleControls(true);

    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.userStep(() => this.next(true)));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.userStep(() => this.prev(true)));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.index, 10) || 0;
        this.userStep(() => this.goTo(i, true, true));
      });
    });

    // Klavye okları (component focus içindeyken)
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.userStep(() => this.prev(true));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.userStep(() => this.next(true));
      }
    });

    // Hover / focus / sekme görünürlüğü → duraklat
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resume();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.resume();
    });

    this.bindSwipe();
  }

  // Kullanıcı etkileşimi: otomatik oynatma tur'unu sıfırla.
  userStep(action) {
    action();
    if (this.autoplay && !this.paused) this.schedule();
  }

  toggleControls(enabled) {
    const display = enabled && this.count > 1 ? '' : 'none';
    [this.prevBtn, this.nextBtn].forEach((el) => {
      if (el) el.style.display = display;
    });
    const dotsWrap = this.querySelector('[data-hero-dots]');
    if (dotsWrap) dotsWrap.style.display = display;
    const progressWrap = this.querySelector('[data-hero-progress-track]');
    if (progressWrap && (!enabled || !this.autoplay)) progressWrap.style.display = 'none';
  }

  /* ---------- Dokunmatik / pointer kaydırma ---------- */
  bindSwipe() {
    const threshold = 0.12; // viewport genişliğinin %12'si
    let startX = 0;
    let startY = 0;
    let basePos = 0;
    let width = 1;
    let locked = null; // 'x' | 'y' | null

    const onDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragging = true;
      locked = null;
      startX = e.clientX;
      startY = e.clientY;
      width = this.viewport.clientWidth || 1;
      basePos = this.pos;
      this.pause();
      if (this.mode === 'slide') this.track.style.transition = 'none';
    };

    const onMove = (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (locked === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (locked === 'x') {
          try {
            this.viewport.setPointerCapture(e.pointerId);
          } catch (_) {}
        }
      }
      if (locked !== 'x') return; // dikey kaydırma → sayfayı bırak

      e.preventDefault();
      if (this.mode === 'slide') {
        const pct = (dx / width) * 100;
        this.track.style.transform = `translate3d(${-basePos * 100 + pct}%, 0, 0)`;
      }
    };

    const onUp = (e) => {
      if (!this.dragging) return;
      this.dragging = false;
      try {
        this.viewport.releasePointerCapture(e.pointerId);
      } catch (_) {}

      const dx = e.clientX - startX;
      const moved = Math.abs(dx) / width;

      // Sürükleme sonrası kazara link/tıklama tetiklenmesini engelle
      this.justDragged = locked === 'x' && Math.abs(dx) > 8;

      if (locked === 'x' && moved > threshold) {
        if (dx < 0) this.next(true);
        else this.prev(true);
      } else if (this.mode === 'slide') {
        this.translate(basePos, true); // geri yapış
      }

      this.paused = false;
      if (this.autoplay) this.schedule();
      locked = null;
    };

    this.viewport.addEventListener('pointerdown', onDown);
    this.viewport.addEventListener('pointermove', onMove, { passive: false });
    this.viewport.addEventListener('pointerup', onUp);
    this.viewport.addEventListener('pointercancel', onUp);

    // Sürüklemeden hemen sonraki tıklamayı yut (tüm-slide linki için)
    this.viewport.addEventListener(
      'click',
      (e) => {
        if (this.justDragged) {
          e.preventDefault();
          e.stopPropagation();
          this.justDragged = false;
        }
      },
      true
    );
  }
}

if (!customElements.get('hoito-hero-banner')) {
  customElements.define('hoito-hero-banner', HoitoHeroBanner);
}
